import React, { useEffect, useState } from "react";
import Layout from "src/layout";
import Container from "@/components/ui/container";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import TableSkeleton from "@/components/ui/tableSkeleton";
import InputUi from "@/components/ui/inputui";
import { listSectionItems, reorderSectionItems } from "src/lib/api/sectionItemsApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    RiImageLine,
    RiAddLine,
    RiRefreshLine,
    RiEditLine,
    RiDeleteBinLine,
    RiCodeSSlashLine,
    RiSearchLine,
    RiDragMoveLine,
    RiCloseLine
} from "react-icons/ri";

const ListSectionItems = () => {
    const [items, setItems] = useState([]);
    const [type, setType] = useState('');
    const [filterId, setFilterId] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedJson, setFeedJson] = useState(null);
    const [jsonLoading, setJsonLoading] = useState(false);
    const navigate = useNavigate();

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await listSectionItems({ type: type || undefined });
            if (res.success) {
                let data = res.data || [];
                if (filterId) {
                    data = data.filter(d => String(d.itemId) === String(filterId) || String(d.id) === String(filterId));
                }
                setItems(data);
            } else {
                toast.error(res.message || "Failed to fetch section items");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch section items");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const onDragStart = (e, id) => {
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const onDrop = async (e, targetId) => {
        e.preventDefault();
        const draggedId = e.dataTransfer.getData('text/plain');
        if (!draggedId) return;

        const list = [...items];
        const getKey = (s) => String(s.entryId || s.id);
        const fromIndex = list.findIndex(i => getKey(i) === String(draggedId));
        const toIndex = list.findIndex(i => getKey(i) === String(targetId));
        if (fromIndex === -1 || toIndex === -1) return;

        const [moved] = list.splice(fromIndex, 1);
        list.splice(toIndex, 0, moved);

        const updated = list.map((it, idx) => ({ ...it, orderIndex: idx }));
        setItems(updated);

        try {
            const payload = updated.map(it => ({ id: it.entryId || it.id, orderIndex: it.orderIndex }));
            const res = await reorderSectionItems(payload);
            if (res.success) {
                toast.success('Sequence order updated');
            } else {
                toast.error(res.message || 'Failed to save order');
                fetchData();
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to save order');
            fetchData();
        }
    };

    return (
        <Layout active="admin-section-items-list" title="Section Items">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiImageLine className="text-indigo-600" /> Homepage Section Items
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Manage layout blocks, presale sections, banners, and drag-to-reorder homepage sequence.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={async () => {
                                    if (!window.confirm('Clear homepage API cache?')) return;
                                    try {
                                        const { clearSectionItemsCache } = await import('src/lib/api/sectionItemsApi');
                                        const res = await clearSectionItemsCache();
                                        if (res.success) {
                                            toast.success('Homepage cache cleared successfully');
                                            fetchData();
                                        } else {
                                            toast.error(res.message || 'Failed to clear cache');
                                        }
                                    } catch (err) {
                                        console.error(err);
                                        toast.error('Failed to clear cache');
                                    }
                                }}
                                variant="outline"
                                className="bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-sm"
                            >
                                <RiRefreshLine className="w-4 h-4 mr-1.5" /> Clear Home Cache
                            </Button>
                            <Button
                                onClick={() => navigate('/section-items/add')}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm font-medium"
                            >
                                <RiAddLine className="w-4 h-4" /> Add Item
                            </Button>
                        </div>
                    </div>

                    {/* Filter Card */}
                    <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Section Type</label>
                                <select
                                    value={type}
                                    onChange={e => setType(e.target.value)}
                                    className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                                >
                                    <option value="">All Section Types</option>
                                    <option value="imagesection">imagesection</option>
                                    <option value="productsection">productsection</option>
                                    <option value="presalesection">presalesection</option>
                                    <option value="featuredcoupon">featuredcoupon</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Filter by Item ID</label>
                                <div className="relative">
                                    <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <InputUi
                                        value={filterId}
                                        datafunction={e => setFilterId(e.target.value)}
                                        placeholder="Item ID"
                                        className="pl-9"
                                    />
                                </div>
                            </div>

                            <div className="flex items-end gap-2">
                                <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm h-10">
                                    Search Items
                                </Button>
                                {(type || filterId) && (
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setType('');
                                            setFilterId('');
                                            fetchData();
                                        }}
                                        className="border-slate-200 text-slate-600 hover:bg-slate-50 h-10"
                                    >
                                        <RiCloseLine className="w-4 h-4 mr-1" /> Clear
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Drag-and-Drop Table */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">Sequence Order ({items.length} items)</h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                                <RiDragMoveLine /> Drag rows to reorder
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader>
                                    <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider w-12">Move</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Entry ID</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Type</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Target Item ID</TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Order Index</TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Title / Name</TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <TableSkeleton columns={7} rows={5} />
                                    ) : items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <RiImageLine className="w-12 h-12 text-slate-300 mb-3" />
                                                    <p className="text-slate-700 font-semibold text-base">No Section Items Found</p>
                                                    <p className="text-slate-400 text-xs mt-1">Add a section item to customize homepage ordering.</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((s) => {
                                            const key = s.entryId || s.id;
                                            return (
                                                <TableRow
                                                    key={key}
                                                    draggable
                                                    onDragStart={(e) => onDragStart(e, String(key))}
                                                    onDragOver={onDragOver}
                                                    onDrop={(e) => onDrop(e, String(key))}
                                                    className="hover:bg-indigo-50/40 cursor-grab active:cursor-grabbing transition-colors"
                                                >
                                                    <TableCell className="px-6 py-4 text-center text-slate-400">
                                                        <RiDragMoveLine className="w-4 h-4 mx-auto" />
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap font-mono font-bold text-slate-900">
                                                        #{key}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap">
                                                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-full text-xs font-semibold">
                                                            {s.type}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap font-mono text-sm text-slate-700">
                                                        {s.itemId || '-'}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center font-bold text-slate-900">
                                                        {s.orderIndex}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-slate-800">
                                                        {s.type === 'featuredcoupon' && s.coupon ? (
                                                            <div className="flex items-center gap-2">
                                                                {s.coupon.iconImage && (
                                                                    <img src={s.coupon.iconImage} alt="coupon" className="w-7 h-7 rounded-full object-cover border" />
                                                                )}
                                                                <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full font-semibold">
                                                                    🎁 {s.coupon.couponCode || 'Featured Coupon'}
                                                                </span>
                                                            </div>
                                                        ) : (s.title || s.name || s.sectionID || '-')}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => navigate(`/section-items/edit/${key}`)}
                                                                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 text-xs px-2.5 py-1"
                                                            >
                                                                <RiEditLine className="w-3.5 h-3.5 mr-1" /> Edit
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={async () => {
                                                                    if (!window.confirm('Delete this section item?')) return;
                                                                    try {
                                                                        const { deleteSectionItem } = await import('src/lib/api/sectionItemsApi');
                                                                        const res = await deleteSectionItem(key);
                                                                        if (res.success) {
                                                                            toast.success('Section item deleted');
                                                                            fetchData();
                                                                        } else {
                                                                            toast.error(res.message || 'Failed to delete');
                                                                        }
                                                                    } catch (err) {
                                                                        console.error(err);
                                                                        toast.error('Failed to delete');
                                                                    }
                                                                }}
                                                                className="text-rose-600 border-rose-200 hover:bg-rose-50 text-xs px-2.5 py-1"
                                                            >
                                                                <RiDeleteBinLine className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    {/* Live JSON Inspector */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div>
                                <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                                    <RiCodeSSlashLine className="text-indigo-600" /> Live Homepage Feed JSON Inspector
                                </h3>
                                <p className="text-xs text-slate-500">Inspect real-time JSON payload sent to user mobile apps</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                        try {
                                            setJsonLoading(true);
                                            const { listSectionItems } = await import('src/lib/api/sectionItemsApi');
                                            const res = await listSectionItems();
                                            setFeedJson(res);
                                            toast.success('Fetched live JSON feed');
                                        } catch (err) {
                                            console.error(err);
                                            toast.error('Failed to fetch JSON feed');
                                        } finally {
                                            setJsonLoading(false);
                                        }
                                    }}
                                    disabled={jsonLoading}
                                    className="border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
                                >
                                    {jsonLoading ? 'Fetching...' : 'Fetch Feed JSON'}
                                </Button>
                                {feedJson && (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(feedJson, null, 2));
                                                toast.success('Copied JSON to clipboard');
                                            }}
                                            className="border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
                                        >
                                            Copy JSON
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setFeedJson(null)}
                                            className="text-slate-500 hover:bg-slate-100"
                                        >
                                            Hide Panel
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        {feedJson && (
                            <div className="p-4 bg-slate-900 text-emerald-400 font-mono text-xs max-h-[400px] overflow-auto leading-relaxed">
                                <pre>{JSON.stringify(feedJson, null, 2)}</pre>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default ListSectionItems;
